// src/components/Sidebar.js

import React, { useState, useEffect, useMemo } from 'react';
import { signOut } from 'firebase/auth';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../App';
import { appId } from '../config/firebaseConfig';
import { PERMISSIONS_CONFIG, ADMIN_EMAIL } from '../config/permissionsConfig';
import { PAGES, MENU_SECTIONS } from '../config/menuConfig';

const SECTIONS_STORAGE_KEY = 'arcus_sidebar_collapsed_sections';

export default function Sidebar({ isOpen, onClose, navigateTo, currentPage, userEmail, userPermissions = {}, loadingPermissions = false, isPinned, onTogglePin }) {
    
    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error("Erro ao sair:", error);
        }
    };

    const isPageAllowed = (pageKey) => userPermissions[pageKey] && userPermissions[pageKey] !== 'none';
    const isManageAccessPageAllowed = isPageAllowed('manage-access');

    // --- Grupos recolhíveis ---
    const [collapsedSections, setCollapsedSections] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY)) || {}; } catch (e) { return {}; }
    });
    const toggleSection = (sectionId) => {
        setCollapsedSections(prev => {
            const next = { ...prev, [sectionId]: !prev[sectionId] };
            try { localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
            return next;
        });
    };

    // No modo "trilho" (desktop, menu recolhido e sem hover) só aparecem ícones: todos os grupos ficam abertos
    const [isHovered, setIsHovered] = useState(false);
    const [isDesktop, setIsDesktop] = useState(() => {
        try { return window.matchMedia('(min-width: 1024px)').matches; } catch (e) { return true; }
    });
    useEffect(() => {
        let mq;
        try { mq = window.matchMedia('(min-width: 1024px)'); } catch (e) { return; }
        const handler = (e) => setIsDesktop(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', handler); else mq.addListener(handler);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', handler); else mq.removeListener(handler); };
    }, []);
    const isRail = isDesktop && !isPinned && !isHovered;

    const spanClass = `font-medium whitespace-nowrap overflow-hidden transition-all duration-300 opacity-100 ml-3 ${
        isPinned ? 'lg:opacity-100 lg:ml-3 lg:max-w-[200px]' : 'lg:opacity-0 lg:group-hover:opacity-100 lg:max-w-0 lg:group-hover:max-w-[200px] lg:ml-0 lg:group-hover:ml-3'
    }`;
    const headerClass = `px-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2 whitespace-nowrap overflow-hidden transition-all duration-300 opacity-100 lg:max-w-[200px] ${
        isPinned ? 'lg:opacity-100' : 'lg:opacity-0 lg:group-hover:opacity-100 lg:max-w-0 lg:group-hover:max-w-[200px]'
    }`;
    
    // Classes unificadas para os botões garantirem alinhamento matemático no centro (76px total -> 44px util)
    const btnClass = `flex items-center justify-center w-full py-3 mb-2 rounded-xl transition-all duration-300 ${
        isPinned ? 'lg:justify-start px-0 lg:px-3' : 'lg:group-hover:justify-start px-0 lg:group-hover:px-3'
    }`;

    return (
        <>
            {isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose}></div>
            )}

            <aside onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className={`fixed inset-y-0 left-0 bg-slate-900 border-r border-slate-800 lg:bg-slate-900/50 lg:backdrop-blur-2xl lg:border lg:border-white/10 lg:rounded-[2rem] lg:top-4 lg:bottom-4 lg:left-4 z-50 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-[width,transform] duration-300 ease-in-out flex flex-col group w-64 flex-shrink-0 shadow-2xl overflow-hidden ${
                isPinned ? 'lg:w-[260px]' : 'lg:w-[76px] lg:hover:w-[260px]'
            }`}>
                
                <div className="p-4 py-6 border-b border-white/10 flex justify-between items-center whitespace-nowrap h-[81px]">
                    <div className="flex items-center">
                        <div className="w-8 h-8 flex-shrink-0 bg-cyan-500 rounded-lg flex items-center justify-center font-bold text-white shadow-lg shadow-cyan-500/20 ml-[6px]">A</div>
                        <h2 className={`ml-3 text-xl font-bold text-white tracking-tight transition-all duration-300 overflow-hidden ${
                            isPinned ? 'opacity-100 lg:max-w-[200px]' : 'opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:max-w-0 lg:group-hover:max-w-[200px]'
                        }`}>Intranet Arcus</h2>
                    </div>
                    
                    {/* Botão de Trava (Padlock) - Apenas Desktop */}
                    <div className={`hidden lg:flex overflow-hidden transition-all duration-300 ${
                        isPinned ? 'w-8 opacity-100 ml-auto' : 'w-0 opacity-0 group-hover:w-8 group-hover:opacity-100 group-hover:ml-auto'
                    }`}>
                        <button 
                            onClick={onTogglePin} 
                            className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg transition-colors border ${isPinned ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' : 'bg-transparent text-slate-500 border-transparent hover:bg-white/5 hover:text-white'}`}
                            title={isPinned ? "Destravar menu" : "Travar menu aberto"}
                        >
                            {isPinned ? (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            ) : (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                </svg>
                            )}
                        </button>
                    </div>

                    <button onClick={onClose} className="lg:hidden flex-shrink-0 text-slate-400 hover:text-white ml-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                {/* Esconde a barra de rolagem quando recolhido para não dar shift nos ícones */}
                <nav className={`flex-1 p-4 overflow-x-hidden overflow-y-auto custom-scrollbar transition-all ${!isPinned ? 'lg:[&:not(:hover)]:overflow-hidden' : ''}`}>
                    {MENU_SECTIONS.map(section => {
                        const visibleItems = section.items.filter(key => PAGES[key] && isPageAllowed(key));
                        if (visibleItems.length === 0) return null; // grupo sem nenhum item liberado não aparece

                        const containsCurrent = visibleItems.includes(currentPage);
                        const isExpanded = !section.label || isRail || containsCurrent || !collapsedSections[section.id];

                        const items = visibleItems.map(key => (
                            <button
                                key={key}
                                onClick={() => { navigateTo(key); onClose(); }}
                                className={`${btnClass} ${currentPage === key ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20' : 'hover:bg-slate-700 text-slate-300'}`}
                                title={PAGES[key].label}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={PAGES[key].icon} /></svg>
                                <span className={spanClass}>{PAGES[key].label}</span>
                            </button>
                        ));

                        if (!section.label) {
                            return <div key={section.id}>{items}</div>;
                        }

                        return (
                            <div key={section.id} className="my-4 border-t border-white/5 pt-4">
                                <button
                                    type="button"
                                    onClick={() => { if (!containsCurrent) toggleSection(section.id); }}
                                    className={`w-full flex items-center justify-between mb-2 pr-1 ${containsCurrent ? 'cursor-default' : 'cursor-pointer'}`}
                                    title={containsCurrent ? 'Grupo da página atual' : (isExpanded ? 'Recolher grupo' : 'Expandir grupo')}
                                >
                                    <p className={headerClass.replace('mb-2 ', '')}>{section.label}</p>
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 flex-shrink-0 text-slate-500 transition-all duration-300 ${isExpanded ? '' : '-rotate-90'} ${isPinned ? 'lg:opacity-100' : 'lg:opacity-0 lg:group-hover:opacity-100'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" /></svg>
                                </button>
                                {isExpanded && items}
                            </div>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-white/10 bg-slate-800/50 lg:bg-transparent overflow-hidden">
                    {isManageAccessPageAllowed && (
                        <button onClick={() => { navigateTo('manage-access'); onClose(); }} className={`flex items-center justify-center w-full py-3 mb-4 rounded-xl transition-all duration-300 ${isPinned ? 'lg:justify-start px-0 lg:px-3' : 'lg:group-hover:justify-start px-0 lg:group-hover:px-3'} ${currentPage === 'manage-access' ? 'bg-cyan-600 text-white' : 'hover:bg-slate-700 text-slate-400'}`}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                            <span className={`text-sm uppercase tracking-tighter ${spanClass}`}>Acessos</span>
                        </button>
                    )}
                    <div className={`px-0 lg:px-3 mb-4 flex flex-col transition-all ${isPinned ? 'items-center lg:items-start' : 'items-center lg:group-hover:items-start'}`}>
                        <p className={headerClass}>Sessão</p>
                        <p className={`text-xs text-slate-300 truncate font-medium ${spanClass}`}>{userEmail}</p>
                    </div>
                    <button onClick={handleLogout} className={`flex items-center justify-center w-full py-3 text-sm font-bold rounded-xl text-white bg-red-600/80 hover:bg-red-600 transition-all uppercase tracking-widest ${isPinned ? 'lg:justify-start px-0 lg:px-3' : 'lg:group-hover:justify-start px-0 lg:group-hover:px-3'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        <span className={spanClass}>Sair</span>
                    </button>
                </div>
            </aside>
        </>
    );
}